'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Button, IconButton, Skeleton, SvgIconWrapper, Typography } from 'lighthouse';
import { ChevronDownOffMdSVG, LocationOnOutlineSmSVG } from 'lighthouse-icons';

import BackButton from '@/widgets/AppBar/_components/BackButton';
import { SelectCityModal } from '@/widgets/AppBar/_components/SelectCityModal';
import LoginModal from '@/widgets/LoginModal';
import { DesktopMenu } from '@/widgets/Navbar/DesktopMenu';
import { ProfileDropdown } from '@/widgets/ProfileDropdown';

import { useProfile } from '@/hooks/user/useProfile';
import { useSelectCity } from '@/hooks/user/useSelectCity';

import { getCartItemsQuantity } from '@/utilities/cart';
import { isUserLogin } from '@/utilities/CheckUserIsLogin';

import { useCartStore } from '@/stores/cart/useCartStore';
import { useCityStore } from '@/stores/city/useCityStore';
import { useSupportStore } from '@/stores/support/useSupportStore';

import { yaldaGameLanding } from '@/constants/routes';

import Cart from '../Cart';
import { appBarProps } from './models';
import classes from './styles.module.css';

import { GetProfileResponseType } from '@/client/services/core/user/models';
import { ArrowDown2, Call, User } from 'iconsax-react';

const AppBar = ({
    title,
    hasBack = true,
    hasProfileIcon = true,
    hasCity = false,
    isDark,
    hasTopBanner,
    hasCart = true,
    showCartOnMobile = false
}: appBarProps) => {
    const pathname = usePathname();
    const { isUserSelectedCity, userCity } = useSelectCity();
    const { fullName, loadProfile, logout, profile, isProfileLoading } = useProfile();

    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isSelectCityModalOpen, setIsSelectCityModalOpen] = useState(false);
    const [isClientReady, setIsClientReady] = useState(false);

    const isLoggedIn = isUserLogin();
    const isGetUserLocation = useCityStore((store) => store.isGetUserLocation);
    const displayedIsLoggedIn = isClientReady ? isLoggedIn : null;
    const cartItemsQuantity = useCartStore((store) => getCartItemsQuantity(store.cart?.items));
    const getCartProducts = useCartStore((store) => store.getCartProducts);
    const openSupport = useSupportStore((store) => store.openSupport);
    const shouldShowCart = isClientReady && isLoggedIn && hasCart && cartItemsQuantity > 0;

    useEffect(() => {
        if (hasCart) {
            void getCartProducts();
        }
    }, [getCartProducts, hasCart, isLoggedIn]);

    const getProfile = async (): Promise<GetProfileResponseType | null> => {
        try {
            const profileResponse = await loadProfile();
            return profileResponse;
        } catch {
            return null;
        } finally {
        }
    };

    useEffect(() => {
        setIsClientReady(true);
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            getProfile();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isUserSelectedCity) {
            setIsSelectCityModalOpen(true);
        }
    }, [isUserSelectedCity, isGetUserLocation]);

    const handleOpenSelectCityModal = async () => {
        setIsSelectCityModalOpen(true);
    };

    return (
        <div className='sticky top-0 z-50'>
            {!hasBack && hasTopBanner && !['/'].includes(pathname) && (
                <Link
                    href={yaldaGameLanding}
                    target='_blank'
                    className='bg-image flex h-[54px] w-full cursor-pointer items-center bg-cover bg-no-repeat'
                    style={{
                        backgroundImage: 'url("/images/campaigns/yalda-bg.webp")',
                        backgroundPosition: '1px center'
                    }}>
                    <Typography variant='subtitle1' color='white' component='div' align='center' className='w-full'>
                        یلدای امسال، بازی کن و تخفیف ببر🍉
                    </Typography>
                </Link>
            )}

            <div className={`relative z-50 flex h-14 w-full items-center shadow-lg md:h-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
                <div className='desktop-width mx-auto flex h-14 items-center gap-2 px-4'>
                    {hasBack ? (
                        <BackButton />
                    ) : (
                        <div className='flex min-w-0 flex-1 items-center justify-start gap-x-2 overflow-visible'>
                            <Link href='/' className='flex shrink-0'>
                                <Image priority width={143} height={24} src='/images/logo/azkiservice-logo.svg' alt='azkiservice logo' />
                            </Link>
                            <DesktopMenu isDark={isDark} />
                        </div>
                    )}
                    {title && (
                        <Typography
                            className='pointer-events-none absolute right-0 w-full text-center'
                            variant='caption'
                            fontWeight={700}
                            color='black'
                            display='table'>
                            {title}
                        </Typography>
                    )}
                    <div className='mr-auto flex shrink-0 items-center gap-2 md:gap-3'>
                        {hasCity && (
                            <>
                                {isClientReady && userCity ? (
                                    <button
                                        onClick={handleOpenSelectCityModal}
                                        className='all-unset flex w-fit shrink-0 cursor-pointer items-center justify-between rounded-lg bg-gray-100
                                            px-2 py-1 text-blackHighEmphasis'>
                                        <SvgIconWrapper className='block md:hidden lg:block' icon={LocationOnOutlineSmSVG} size='sm' />
                                        <span className='ml-1 mr-1'>{userCity.name}</span>
                                        <SvgIconWrapper icon={ChevronDownOffMdSVG} size='md' />
                                    </button>
                                ) : (
                                    <Skeleton className='h-8 w-24 scale-100 md:h-8 md:w-16 lg:h-8 lg:w-24' />
                                )}
                                {isSelectCityModalOpen && <SelectCityModal onClose={() => setIsSelectCityModalOpen(false)} />}
                            </>
                        )}
                        {hasProfileIcon && (
                            <div className='hidden shrink-0 items-center md:flex'>
                                <div className='flex items-center'>
                                    {displayedIsLoggedIn == null ? (
                                        <div
                                            className='flex h-8 w-32 animate-pulse rounded-full bg-gray-200 md:h-8 md:w-10 md:rounded-lg lg:h-8
                                                lg:w-32 lg:rounded-full'></div>
                                    ) : displayedIsLoggedIn ? (
                                        <>
                                            <Button
                                                disableRipple
                                                onClick={() => setIsProfileMenuOpen(true)}
                                                variant='outlined'
                                                rounded
                                                className={`${isDark ? 'text-white' : 'text-primary'} hidden lg:flex`}
                                                endIcon={<ArrowDown2 color='currentcolor' size={14} className='stroke-primary' />}>
                                                حساب کاربری
                                            </Button>
                                            <IconButton
                                                disableRipple
                                                onClick={() => setIsProfileMenuOpen(true)}
                                                className='inline-flex h-10 w-10 items-center justify-center p-2 leading-none lg:hidden'
                                                aria-label='حساب کاربری'>
                                                <User
                                                    variant='Broken'
                                                    color='currentcolor'
                                                    size={24}
                                                    className={`${isDark ? 'text-white' : 'text-blackMediumEmphasis'} `}
                                                />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                onClick={() => setIsLoginModalOpen(true)}
                                                variant='outlined'
                                                rounded
                                                className={`${isDark ? 'text-white' : 'text-primary'} md:hidden lg:block`}>
                                                ورود / ثبت نام
                                            </Button>
                                            <IconButton
                                                onClick={() => setIsLoginModalOpen(true)}
                                                className='inline-flex h-10 w-10 items-center justify-center p-2 leading-none lg:hidden'
                                                aria-label='ورود / ثبت نام'>
                                                <User
                                                    variant='Broken'
                                                    color='currentcolor'
                                                    size={24}
                                                    className={`${isDark ? 'text-white' : 'text-blackMediumEmphasis'}`}
                                                />
                                            </IconButton>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        {shouldShowCart ? (
                            <div className={showCartOnMobile ? 'block shrink-0' : 'hidden shrink-0 md:block'}>
                                <Cart />
                            </div>
                        ) : (
                            <Button
                                aria-label='پشتیبانی'
                                variant='text'
                                rounded
                                onClick={() => openSupport(window.matchMedia('(min-width: 768px)').matches ? 'app_bar_desktop' : 'app_bar_mobile')}
                                className={`min-h-10 min-w-10 shrink-0 px-2 ${isDark ? 'text-white' : 'text-primary'}`}>
                                <span className='flex flex-row items-center' dir='rtl'>
                                    <Call color='currentColor' size={24} className='shrink-0' aria-hidden='true' />
                                    <span className={`${classes.supportLabel} overflow-hidden whitespace-nowrap text-sm`} aria-hidden='true'>
                                        <span className='block pr-2'>پشتیبانی</span>
                                    </span>
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {displayedIsLoggedIn && isProfileMenuOpen && (
                <ProfileDropdown
                    logout={logout}
                    userProfileData={profile}
                    userProfileLoading={isProfileLoading}
                    userFullName={fullName}
                    open={isProfileMenuOpen}
                    onClose={() => setIsProfileMenuOpen(false)}
                />
            )}
            {!displayedIsLoggedIn && isLoginModalOpen && (
                <LoginModal hasCloseButton onClose={() => setIsLoginModalOpen(false)} onUserLogin={() => setIsLoginModalOpen(false)} />
            )}
        </div>
    );
};

export default AppBar;
